import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CategoryForm from '../../components/categories/CategoryForm';
import categoryService from '../../services/categoryService';
import './CategoryFormPage.css';

function CategoryFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all categories for duplicate slug validation
      const categories = await categoryService.getCategories();
      setAllCategories(categories);

      // If editing, fetch the specific category
      if (isEditing) {
        const categoryData = await categoryService.getCategory(id);
        setCategory(categoryData);
      }
    } catch (err) {
      setError(err.message || 'Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      // Validate data
      const validation = categoryService.validateCategory(data, allCategories, category?.id);
      if (!validation.isValid) {
        alert(`Validation Error:\n\n${Object.values(validation.errors).join('\n')}`);
        return;
      }

      // Prepare data for submission
      const submitData = {
        name: data.name.trim(),
        slug: data.slug.trim(),
        description: data.description.trim(),
        icon: data.icon?.trim() || null,
        isActive: data.isActive,
        displayOrder: parseInt(data.displayOrder) || 0,
      };

      if (isEditing) {
        await categoryService.updateCategory(id, submitData);
      } else {
        await categoryService.createCategory(submitData);
      }

      // Navigate back to list
      navigate('/categories');
    } catch (err) {
      alert(`Failed to ${isEditing ? 'update' : 'create'} category: ${err.message}`);
      console.error('Error submitting form:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/categories');
  };

  if (loading) {
    return (
      <div className="category-form-page">
        <div className="page-loading">
          <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-form-page">
        <div className="page-error">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={handleCancel} className="btn btn-primary">
            Back to Categories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="category-form-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <button onClick={handleCancel} className="back-button" aria-label="Back to categories">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <div>
            <h1>{isEditing ? 'Edit Category' : 'New Category'}</h1>
            <p className="header-subtitle">
              {isEditing
                ? `Update information for ${category?.name}`
                : 'Create a new category for your products'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="form-card">
        <CategoryForm
          category={category}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />

        {/* Cancel Button */}
        <div className="form-footer">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default CategoryFormPage;
